import { useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { isDemoMode } from "../../lib/api";

export function RequireAuth({ admin = false }: { admin?: boolean }) {
  const location = useLocation();
  const [state, setState] = useState<"loading" | "allowed" | "blocked" | "redirect" | "unauthorized" | "unavailable">("loading");
  const [redirectTo, setRedirectTo] = useState("");

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeoutMs = 8000;
    const complete = (nextState: "allowed" | "blocked" | "redirect" | "unauthorized" | "unavailable", nextRedirectTo = "") => {
      if (!active) return;
      active = false;
      window.clearTimeout(timeoutId);
      setRedirectTo(nextRedirectTo);
      setState(nextState);
    };
    const timeoutId = window.setTimeout(() => {
      controller.abort();
      complete(admin ? "blocked" : "unavailable");
    }, timeoutMs);

    const check = async () => {
      // In local development we allow an explicit demo-auth flag to skip the network session check.
      // Production never sees this branch because import.meta.env.DEV is compile-time false there.
      const demoAuth = import.meta.env.DEV ? localStorage.getItem("ktd_demo_auth") : isDemoMode ? localStorage.getItem("ktd_demo_auth") : null;
      if (demoAuth === (admin ? "admin" : "client")) {
        complete("allowed");
        return;
      }
      try {
        const response = await fetch("/api/auth/session", {
          credentials: "include",
          signal: controller.signal,
        });
        if (response.status === 401) {
          complete("blocked");
          return;
        }
        if (!response.ok) {
          complete(admin ? "blocked" : "unavailable");
          return;
        }
        const data = (await response.json()) as {
          user?: {
            email?: string;
            name?: string;
            role?: string;
            productAccess?: {
              collabRecoverClientPortal?: boolean;
              memoryMediaVault?: boolean;
            } | null;
          } | null;
        };
        if (!data.user) {
          complete("blocked");
          return;
        }
        const role = typeof data.user.role === "string" ? data.user.role : "";
        const internalRoles = ["kreatethedate_admin", "admin", "manager", "supervisor", "team_member", "viewer"];
        const clientPortalAdminRoute = location.pathname === "/admin/ar-recovery/client-portal";
        const allowed = admin
          ? internalRoles.includes(role) || (clientPortalAdminRoute && role === "client")
          : role === "client" || role === "viewer" || role === "kreatethedate_admin";
        if (!admin && !allowed) {
          complete("unauthorized");
          return;
        }
        if (!admin && !hasClientProductAccess(location.pathname, data.user.productAccess || null)) {
          complete("unauthorized");
          return;
        }
        const restrictedRedirect = admin && allowed ? getRestrictedAdminRouteRedirect(role, location.pathname) : "";
        if (restrictedRedirect) {
          complete("redirect", restrictedRedirect);
          return;
        }
        complete(allowed ? "allowed" : "blocked");
      } catch {
        complete(admin ? "blocked" : "unavailable");
      }
    };
    void check();
    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [admin, location.pathname]);

  if (state === "loading") {
    return (
      <div className="grid min-h-screen place-items-center bg-ktd-black text-white">
        <div className="luxe-card rounded-lg p-8 text-center">
          <p className="font-display text-3xl font-bold gold-text">Checking admin session...</p>
          <p className="mt-3 text-sm text-white/62">Checking secure access...</p>
        </div>
      </div>
    );
  }

  if (state === "blocked") {
    return <Navigate to={admin ? "/admin/login" : "/login"} replace state={{ from: location }} />;
  }

  if (state === "redirect") {
    return <Navigate to={redirectTo || "/admin/ar-recovery"} replace />;
  }

  if (state === "unauthorized") {
    return (
      <main className="grid min-h-screen place-items-center bg-ktd-black px-6 text-white">
        <section className="w-full max-w-md rounded-2xl border border-white/12 bg-white/[0.06] p-8 text-center shadow-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#1fd6c2]">Product access</p>
          <h1 className="mt-4 font-display text-3xl font-bold">Your account is signed in, but it does not currently have access to this Client Portal.</h1>
          <p className="mt-3 text-sm text-white/70">Contact your CollabRecover administrator or support to request access.</p>
          <Link
            to="/login"
            replace
            className="mt-6 inline-flex rounded-full border border-[#1fd6c2]/55 px-5 py-2 text-sm font-bold text-white transition hover:bg-[#1fd6c2]/16 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1fd6c2]"
          >
            Return to secure access
          </Link>
        </section>
      </main>
    );
  }

  if (state === "unavailable") {
    return (
      <main className="grid min-h-screen place-items-center bg-ktd-black px-6 text-white">
        <section className="w-full max-w-md rounded-2xl border border-white/12 bg-white/[0.06] p-8 text-center shadow-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#1fd6c2]">Secure access</p>
          <h1 className="mt-4 font-display text-3xl font-bold">Client Portal access is temporarily unavailable.</h1>
          <p className="mt-3 text-sm text-white/70">Your sign-in was not cleared. Please try again shortly or contact support.</p>
        </section>
      </main>
    );
  }

  return <Outlet />;
}

function getRestrictedAdminRouteRedirect(role: string, pathname: string) {
  const normalizedRole = role.trim().toLowerCase();
  const privilegedRoles = new Set(["kreatethedate_admin", "admin"]);
  if (privilegedRoles.has(normalizedRole)) return "";

  const restrictedRoute =
    pathname === "/admin/business-center-os" ||
    pathname.startsWith("/admin/business-center-os/") ||
    pathname === "/admin/forms-directory" ||
    pathname.startsWith("/admin/forms-directory/");
  if (!restrictedRoute) return "";

  if (normalizedRole === "manager" || normalizedRole === "supervisor") return "/admin/ar-recovery/manager-command-center";
  if (normalizedRole === "team_member") return "/admin/ar-recovery/customer-accounts";
  return "/admin/ar-recovery";
}

function hasClientProductAccess(
  pathname: string,
  productAccess: { collabRecoverClientPortal?: boolean; memoryMediaVault?: boolean } | null,
) {
  const canUseCollabRecoverClientPortal = Boolean(productAccess?.collabRecoverClientPortal);
  const canUseMemoryMediaVault = Boolean(productAccess?.memoryMediaVault);
  if (isCollabRecoverClientPortalRoute(pathname)) return canUseCollabRecoverClientPortal;
  if (isMemoryMediaVaultClientRoute(pathname)) return canUseMemoryMediaVault;
  return true;
}

function isCollabRecoverClientPortalRoute(pathname: string) {
  return pathname === "/client/portal" || pathname.startsWith("/client/portal/");
}

function isMemoryMediaVaultClientRoute(pathname: string) {
  return (
    pathname === "/client/dashboard" ||
    pathname === "/client/memory-hub/vault" ||
    pathname === "/client/pending" ||
    pathname === "/client/approved" ||
    pathname === "/client/featured" ||
    pathname === "/client/hidden" ||
    pathname === "/client/not-approved" ||
    pathname === "/client/upload-link" ||
    pathname === "/client/storage-billing" ||
    pathname === "/client/settings" ||
    pathname === "/client/instructions"
  );
}
